if (!customElements.get('sticky-product-bar')) {
  customElements.define(
    'sticky-product-bar',
    class StickyProductBar extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.rootUrl = this.dataset.rootUrl || '/';
      }

      connectedCallback() {
        this.trigger = document.getElementById(`buy-buttons-${this.sectionId}`);
        this.realSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
        this.atcButton = this.querySelector(`#StickyAddToCart-${this.sectionId}`);
        this.buyNowButton = this.querySelector(`#StickyBuyNow-${this.sectionId}`);
        this.sizeSelect = this.querySelector(`#StickySize-${this.sectionId}`);
        this.priceNode = this.querySelector(`#StickyPrice-${this.sectionId}`);
        this.mediaNode = this.querySelector(`#StickyMedia-${this.sectionId}`);

        this.observeTrigger();
        this.observeSubmitButton();
        this.observeVariantChange();
        // Route the sticky Add to Cart click through the real, already-working
        // submit button instead of relying solely on the button's `form=""`
        // attribute — some mobile in-app browsers (Instagram/Facebook/TikTok
        // webviews) don't reliably submit a form from a button outside it.
        this.atcButton?.addEventListener('click', this.handleAddToCart.bind(this));
        this.buyNowButton?.addEventListener('click', this.handleBuyNow.bind(this));
        this.sizeSelect?.addEventListener('change', this.handleSizeChange.bind(this));
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      observeTrigger() {
        if (!this.trigger || !('IntersectionObserver' in window)) return;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            // show only once the trigger has scrolled above the viewport,
            // hide again if it's back in view or still below the viewport
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.toggleAttribute('hidden', !scrolledPast);
          },
          { threshold: 0 }
        );

        this.intersectionObserver.observe(this.trigger);
      }

      observeSubmitButton() {
        if (!this.realSubmitButton) return;

        const sync = () => {
          const disabled =
            this.realSubmitButton.hasAttribute('disabled') ||
            this.realSubmitButton.getAttribute('aria-disabled') === 'true';
          const label = this.realSubmitButton.querySelector('span')?.textContent?.trim();

          [this.atcButton, this.buyNowButton].forEach((button) => {
            if (!button) return;
            button.disabled = disabled;
          });

          if (label && this.atcButton) {
            const atcLabel = this.atcButton.querySelector('span');
            if (atcLabel) atcLabel.textContent = label;
          }
        };

        sync();
        this.mutationObserver = new MutationObserver(sync);
        this.mutationObserver.observe(this.realSubmitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
        });
      }

      observeVariantChange() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;

          const { html, variant } = event.data;

          const sourcePrice = html.getElementById(`price-${this.sectionId}`);
          if (sourcePrice && this.priceNode) this.priceNode.innerHTML = sourcePrice.innerHTML;

          const src = variant?.featured_media?.preview_image?.src || variant?.featured_image?.src;
          if (src && this.mediaNode) {
            this.mediaNode.src = `${src}${src.includes('?') ? '&' : '?'}width=100`;
          }

          this.syncSizeSelect();
        });
      }

      // Keep the sticky size dropdown in sync if the size is instead changed
      // from the main product form's own variant picker.
      syncSizeSelect() {
        if (!this.sizeSelect || !this.variantSelects) return;

        // Scope the match to value ids that belong to the Size option (the
        // ones listed in the sticky select) so a Color option checked/
        // selected earlier in the DOM isn't picked up by mistake.
        const stickyValueIds = new Set(
          Array.from(this.sizeSelect.options).map((opt) => opt.dataset.optionValueId)
        );
        const checkedEls = this.variantSelects.querySelectorAll('fieldset input:checked, select option[selected]');
        const match = Array.from(checkedEls).find((el) => stickyValueIds.has(el.dataset.optionValueId));
        if (!match) return;

        const option = Array.from(this.sizeSelect.options).find(
          (opt) => opt.dataset.optionValueId === match.dataset.optionValueId
        );
        if (option && this.sizeSelect.value !== option.value) {
          this.sizeSelect.value = option.value;
        }
      }

      // Selecting a size in the sticky bar drives the real variant picker so
      // the existing variant-change pipeline (price, media, variant id,
      // availability) runs exactly as it does from the main product form.
      handleSizeChange(event) {
        if (!this.variantSelects) return;

        const optionValueId = event.target.selectedOptions[0]?.dataset.optionValueId;
        if (!optionValueId) return;

        const target = this.variantSelects.querySelector(`[data-option-value-id="${optionValueId}"]`);
        if (!target) return;

        if (target.tagName === 'OPTION') {
          const select = target.closest('select');
          if (!select || select.value === target.value) return;
          select.value = target.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (target.tagName === 'INPUT') {
          if (target.checked) return;
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      handleAddToCart(event) {
        if (!this.realSubmitButton) return;

        event.preventDefault();
        if (this.realSubmitButton.hasAttribute('disabled') || this.realSubmitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }
        this.realSubmitButton.click();
      }

      handleBuyNow() {
        const form = document.getElementById(this.productFormId);
        if (!form || !window.routes?.cart_add_url) return;

        this.buyNowButton.disabled = true;

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status) {
              this.buyNowButton.disabled = false;
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
              return;
            }

            const checkoutBase = this.rootUrl.replace(/\/$/, '');
            window.location.href = `${checkoutBase}/checkout`;
          })
          .catch(() => {
            this.buyNowButton.disabled = false;
          });
      }
    }
  );
}
